import { EnvironmentNames } from "@origin-digital/platform-enums";

export const groupBy = <T>(
  array: T[],
  getKey: (value: T) => string | undefined
): Record<string, T[]> =>
  array.reduce((accumulator: Record<string, T[]>, item) => {
    const key = getKey(item);
    if (isPresent(key)) {
      const existingGroup = accumulator[key];
      if (existingGroup) {
        existingGroup.push(item);
      } else {
        accumulator[key] = [item];
      }
    }
    return accumulator;
  }, {}) ?? {};

export const getFirst = <T>(array: T[]): T | undefined =>
  array?.[0] ?? undefined;

export const isPresent = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

export const shallowCopy = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return [...value] as T;
  } else if (value && typeof value === "object") {
    return { ...value };
  }
  return value;
};

export const deepCopy = <T>(value: T): T => {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => deepCopy(item)) as unknown as T;
  }

  const result: Record<string | symbol, unknown> = {};

  for (const key of Reflect.ownKeys(value)) {
    result[key] = deepCopy((value as any)[key]);
  }

  return result as T;
};

export const API_GATEWAY_HOST: Record<EnvironmentNames, string> = {
  dev: "https://api-dev.rx.originenergy.com.au",
  local: "https://api-dev.rx.originenergy.com.au",
  prod: "https://api.rx.originenergy.com.au",
  staging: "https://api-test.rx.originenergy.com.au",
  test: "https://api-test.rx.originenergy.com.au",
  regress: "https://api-test.rx.originenergy.com.au",
};

/**
 * Adds or updates the given query param in the URL.
 */
export const setQueryParam = (key: string, value: string) => {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.replaceState({}, "", url.toString());
};
